import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Bulk dividend, bond-coupon and ADR-fee runs from the GTN cash statement.
 *
 * GTN holds our clients' securities in one omnibus account, so a dividend
 * arrives as a single aggregate credit and one statement line — nothing reaches
 * the individual client. A run reads that statement, works out who held each
 * security ON ITS RECORD DATE from the daily portfolio snapshots, applies GTN's
 * per-share and withholding rates to each holder's OWN position, and credits
 * each holder `Amount - Tax`.
 *
 * An **ADR fee is the same run in the opposite direction**: the depositary bank
 * charges a fee on ADR holdings, GTN takes it out of the omnibus account in one
 * lump, and every holder on the record date owes a share of it. Same upload,
 * same preview, same run. Three things differ and all three reach the UI:
 *  - the ledger `quantity` is NEGATIVE, while everything the API reports about
 *    the run — preview lines, totals — stays a POSITIVE magnitude. Nothing here
 *    ever negates a number; direction is carried by the wording;
 *  - there is no funds check, so a fee CAN take a client's balance negative.
 *    `INSUFFICIENT_BALANCE` warns how many holders that covers and does not
 *    block;
 *  - no push notification is sent, matching `fee` and `custody_fee`.
 *
 * Three things about it drive the whole UI:
 *  - **There is no undo.** A payment to the wrong holder set has to be unwound
 *    with one `POST /api/admin/adjustment` per client. The preview IS the
 *    safety mechanism, which is why nothing here can execute a payload the
 *    preview did not just authorise.
 *  - **GTN's numbers never determine what a client is paid.** We pay our own
 *    holders their own share counts; GTN's aggregate is never scaled to fit.
 *  - **A re-run is safe.** Every credit is keyed on `eventKey` behind a unique
 *    index, so a second attempt reports holders as `already-paid` rather than
 *    paying twice.
 */

export type CorporateActionType = 'dividend' | 'coupon' | 'adr_fee';

/** Terminal statuses plus `running`, which also covers a crashed run. */
export type CorporateActionRunStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

/** Where a line came from: the parsed statement, or typed in by the desk. */
export type CorporateActionOrigin = 'gtn' | 'manual';

/** Which basis the holder set was resolved from. */
export type HolderBasis = 'snapshot' | 'current';

/** How a line found its instrument. */
export type InstrumentResolvedBy = 'isin' | 'isin+symbol' | 'instrumentInternalId';

/**
 * Which of the three share counts `gtnShares` ended up being. `narration` is
 * the ADR fee case and only that: its stated count DOES describe our position,
 * and having no tax row it is the only stated count a fee has.
 */
export type GtnSharesSource = 'tax_row' | 'cash' | 'narration';

/**
 * How an ADR fee's per-share rate was arrived at. Dialect A states a flat
 * per-share fee outright. Dialect B states a percentage OF THAT SECURITY'S
 * DIVIDEND — which cannot be multiplied by a share count — so the rate is
 * divided back out of GTN's own cash. Show this on the confirmation screen: on
 * a `derived_from_cash` line the rate being approved is one the statement never
 * wrote down.
 */
export type AdrFeePerShareSource = 'narration' | 'derived_from_cash';

/**
 * An instrument the parser resolved, or one of several candidates on an
 * `AMBIGUOUS_ISIN` blocker. One ISIN routinely maps to several listings
 * (`US0126531013` is both `ALB~NYSE` in USD and `AMCD~CHIX` in EUR), so the
 * listing — not just the security — is what has to be identified.
 */
export interface CorporateActionInstrument {
  instrumentInternalId?: string;
  tickerId?: string;
  sourceId?: string;
  isin?: string;
  shortName?: string;
  longName?: string;
  priceCurrencyId?: string;
  status?: string;
}

export type CorporateActionBlockerCode =
  | 'INSTRUMENT_NOT_FOUND'
  | 'AMBIGUOUS_ISIN'
  | 'NO_SNAPSHOT'
  | 'NO_HOLDERS'
  | 'INVALID_LINE'
  | 'UNKNOWN_CURRENCY'
  | 'DUPLICATE_LINE'
  | 'RUN_IN_PROGRESS'
  | 'GTN_FIGURES_INCONSISTENT'
  | 'TOO_MANY_HOLDERS';

export type CorporateActionWarningCode =
  | 'SHARE_COUNT_MISMATCH'
  | 'ALREADY_PAID'
  | 'CURRENT_BALANCE_FALLBACK'
  | 'CURRENCY_INFERRED'
  | 'CURRENCY_DIFFERS_FROM_INSTRUMENT'
  | 'SYMBOL_DIFFERS_FROM_INSTRUMENT'
  | 'NO_TAX_WITHHELD'
  | 'INSUFFICIENT_BALANCE'
  | 'CURRENCY_INACTIVE'
  | 'ZERO_NET'
  | 'HOLDERS_EXCLUDED'
  | 'ALL_HOLDERS_EXCLUDED';

export type CorporateActionIssueCode =
  | CorporateActionBlockerCode
  | CorporateActionWarningCode;

/**
 * A blocker or a warning. Always branch on `code` — the messages are prose and
 * are not part of the contract. Extra fields ride along per code
 * (`SHARE_COUNT_MISMATCH` carries the two counts and their difference).
 */
export interface CorporateActionIssue {
  code: CorporateActionIssueCode;
  message?: string;
  /** `SHARE_COUNT_MISMATCH`. Decimal strings — render, do not compute. */
  gtnShares?: string;
  ourShares?: string;
  difference?: string;
  /** `GTN_FIGURES_INCONSISTENT`. The rate the cash and the stated count imply. */
  impliedPerShare?: string;
  /** `AMBIGUOUS_ISIN`. Every listing the ISIN matched, so one can be picked. */
  candidates?: CorporateActionInstrument[];
  /** `INSUFFICIENT_BALANCE`. How many holders the debit takes negative. */
  affectedHolders?: number;
  [key: string]: unknown;
}

// --- parse -----------------------------------------------------------------

export interface ParseStatementPayload {
  type: CorporateActionType;
  /** Base64 of the .xlsx. There is no multipart endpoint anywhere in this app. */
  file: string;
  fileName: string;
  /** Both filter on RECORD DATE — how nine months of statement is narrowed to one run. */
  fromDate?: string;
  toDate?: string;
}

export interface CorporateActionSourceFile {
  name: string;
  sha256: string;
  rowCount: number;
}

/**
 * One dividend, coupon or ADR fee event off the statement.
 *
 * Only these fields are accepted back on preview/execute — the parse response's
 * diagnostic fields (`dialect`, `sourceRows`, `gtnNarrationShares`, ...) are
 * REJECTED if echoed back, which is what `toSubmittableLine` exists to enforce.
 */
export interface CorporateActionLine {
  /** `type|isin|recordDate|perShare`. Identity of the event, and the idempotency key. */
  eventKey?: string;
  symbol?: string;
  isin?: string;
  /** Required on a manual line when `isin` alone is ambiguous across venues. */
  instrumentInternalId?: string;
  /** Decimal string. */
  perShare?: string;
  /** Null on the newer narration dialect; the preview infers it and raises CURRENCY_INFERRED. */
  currency?: string | null;
  recordDate?: string;
  /** Defaults to recordDate + 1; overridable per line. See `holderBasis`. */
  snapshotDate?: string;
  holderBasis?: HolderBasis;
  /**
   * Decimal string, 0-100. Editable — this is how a non-standard withholding is
   * corrected. Always `0` on an ADR fee, which withholds nothing by nature.
   */
  taxRatePercent?: string;
  /** Always false on an ADR fee: a fee row has no `DIVTAX` row to pair with. */
  hasTaxRow?: boolean;
  /**
   * ADR fee only. Which dialect stated the rate — and therefore whether the
   * operator is approving a rate the statement wrote down or one derived from
   * GTN's own cash.
   */
  perShareSource?: AdrFeePerShareSource;
  /**
   * ADR fee, dialect B only. The percentage OF THE DIVIDEND the statement
   * stated. Carried for traceability and never multiplied by anything — the
   * per-share rate in `perShare` is what a holder is charged on.
   */
  feeRatePercent?: string | null;
  /**
   * False when the statement gave no record date and it was recovered from the
   * row's `On <date>`. Every dialect-A ADR fee row is in this position.
   */
  recordDateExplicit?: boolean;
  /**
   * The share count to display: the `DIVTAX` row's stated count when there is
   * one, else `gtnGrossAmount / perShare` — and on an ADR fee, which has no tax
   * row, the count stated on the `ADRFEE` row. All three describe OUR position.
   */
  gtnShares?: string;
  /** Which of the counts `gtnShares` came from. */
  gtnSharesSource?: GtnSharesSource;
  gtnSharesFromCash?: string;
  /** Stated on the `DIVTAX` row. OURS, and what `gtnShares` prefers. */
  gtnTaxRowShares?: string;
  /**
   * Stated on the `DEPDIV` row of the newer dialect, and **GTN-wide rather than
   * ours** — it disagrees with the cash on 15 of 16 such lines in the reference
   * statement. Never render this as the share count on a dividend; it is here
   * so a discrepancy can be investigated.
   *
   * **An ADR fee is the exception**: the count stated on its `ADRFEE` row IS our
   * position, and it is the count `gtnShares` uses (`gtnSharesSource:
   * 'narration'`).
   */
  gtnNarrationShares?: string | null;
  /**
   * GTN's per-share rate does not reconcile with GTN's own cash. Preview turns
   * this into a `GTN_FIGURES_INCONSISTENT` blocker, because `perShare` is what
   * every client payout is computed from.
   */
  gtnFiguresInconsistent?: boolean;
  gtnGrossAmount?: string;
  gtnTaxAmount?: string;
  /** The instrument the parser resolved this line to, when it could. */
  instrument?: CorporateActionInstrument;
  resolvedBy?: InstrumentResolvedBy;
  /**
   * The raw statement text. Stored on the run record, and the fastest way for
   * an operator to spot a line the parser read wrongly.
   */
  narration?: string;
  taxNarration?: string;
  dialect?: string;
  sourceRows?: unknown;
  origin?: CorporateActionOrigin;
  /**
   * Holders the desk chose to skip on this event. They get no ledger, so a
   * later run of the same line pays them — everyone else comes back
   * `already-paid`. Part of the line, so it is part of the preview signature.
   */
  excludedUserIds?: string[];
}

export interface ParseStatementResponse {
  type: CorporateActionType;
  sourceFile: CorporateActionSourceFile;
  lines: CorporateActionLine[];
  /**
   * Rows that ARE dividend or tax rows but whose wording the parser did not
   * recognise. Normally empty. Non-empty means GTN changed its narration format
   * and THOSE PAYMENTS WILL NOT BE MADE — surface it, never hide it.
   */
  unparsed: unknown[];
  ignoredRows: number;
  /** Every transaction code in the file, so a brand-new code is visible rather than absorbed. */
  codeCounts: Record<string, number>;
}

// --- preview ---------------------------------------------------------------

export interface PreviewCorporateActionPayload {
  type: CorporateActionType;
  lines: CorporateActionLine[];
  allowCurrentBalanceFallback?: boolean;
  /**
   * Downgrades `GTN_FIGURES_INCONSISTENT` to a warning. Correcting `perShare`
   * to the `impliedPerShare` the blocker carries is the intended fix; this is
   * for when the desk has established GTN's rate was the right one.
   */
  allowInconsistentGtnFigures?: boolean;
}

export interface CorporateActionSampleHolder {
  userId: string;
  /** Decimal strings. */
  shares: string;
  grossAmount: string;
  taxAmount: string;
  netAmount: string;
  alreadyPaid?: boolean;
  excluded?: boolean;
  email?: string | null;
  name?: string | null;
  brokerageAccount?: string | null;
}

/** A line resolved against our own book. Money fields are decimal strings. */
export interface CorporateActionPreviewLine extends CorporateActionLine {
  /**
   * Holders who will be paid — skipped holders are not counted here or in the
   * money fields.
   */
  holders?: number;
  /** Everyone who held the security, skipped or not. */
  totalHolders?: number;
  /** How many of `excludedUserIds` actually hold the security. */
  excludedHolders?: number;
  ourShares?: string;
  grossAmount?: string;
  taxAmount?: string;
  netAmount?: string;
  /** First 20 holders only; `sampleOf` is how many there really are. */
  sample?: CorporateActionSampleHolder[];
  sampleOf?: number;
  warnings?: CorporateActionIssue[];
  blockers?: CorporateActionIssue[];
}

export interface CorporateActionTotals {
  lines?: number;
  holders?: number;
  paid?: number;
  skipped?: number;
  failed?: number;
  /** Run record: holders the desk skipped. Kept apart from `skipped`, which the system decides. */
  excluded?: number;
  /** Preview: holders the desk skipped, across every line. */
  excludedHolders?: number;
  grossAmount?: string;
  taxAmount?: string;
  netAmount?: string;
}

export interface CorporateActionPreview {
  lines: CorporateActionPreviewLine[];
  /** Optional defensively — an error envelope can come back without it. */
  totals?: CorporateActionTotals;
  blockers: CorporateActionIssue[];
  warnings: CorporateActionIssue[];
  /** False whenever `blockers` is non-empty; execute enforces the same list. */
  canRun: boolean;
}

// --- holders of one line ---------------------------------------------------

export type CorporateActionHolderFilter =
  | 'all'
  | 'payable'
  | 'excluded'
  | 'flagged';

/**
 * Why the run will not pay this holder, worked out ahead of time with the
 * run's own rules. `null` means they will be paid.
 */
export type CorporateActionPredictedSkip =
  | 'excluded'
  | 'zero-net'
  | 'already-paid'
  | 'user-not-found'
  | 'kyc-not-approved';

export interface PreviewHoldersPayload {
  type: CorporateActionType;
  /** One line, exactly as it goes to preview — skip list included. */
  line: CorporateActionLine;
  page: number;
  itemsPerPage: number;
  /** Matches email, name, brokerage account or userId. */
  keyword?: string;
  filter?: CorporateActionHolderFilter;
  allowCurrentBalanceFallback?: boolean;
  allowInconsistentGtnFigures?: boolean;
}

export interface CorporateActionHolder {
  userId: string;
  email: string | null;
  name: string | null;
  brokerageAccount: string | null;
  kycStatus: string | null;
  /** Decimal strings, positive magnitudes on every type. */
  shares: string;
  grossAmount: string;
  taxAmount: string;
  netAmount: string;
  alreadyPaid: boolean;
  excluded: boolean;
  /** ADR fee only: the charge takes this holder negative. */
  insufficientBalance: boolean;
  predictedSkip: CorporateActionPredictedSkip | null;
  /**
   * The same, ignoring the desk's own skip — whether un-skipping this holder
   * would actually pay them.
   */
  systemSkip: Exclude<CorporateActionPredictedSkip, 'excluded'> | null;
}

export interface CorporateActionHolderSummary {
  totalHolders: number;
  /** Will actually be paid: not skipped for any reason. */
  payable: number;
  excluded: number;
  alreadyPaid: number;
  kycBlocked: number;
  zeroNet: number;
  insufficientBalance: number;
}

export interface PreviewHoldersResponse {
  total: number;
  items: CorporateActionHolder[];
  /** Every id the filter and keyword matched, across all pages. */
  matchingUserIds: string[];
  summary: CorporateActionHolderSummary;
  eventKey?: string | null;
  currency?: string | null;
  blockers: CorporateActionIssue[];
  warnings: CorporateActionIssue[];
}

// --- execute ---------------------------------------------------------------

export interface RunCorporateActionPayload
  extends PreviewCorporateActionPayload {
  /**
   * Required by the backend. Without it the call is refused with
   * ERROR_VALIDATION and nothing happens, so a double-submitted or replayed
   * request cannot pay a book twice.
   */
  confirm: true;
  note?: string;
  /**
   * Defaults to `true`. Send `false` only when 1C has already booked the
   * corporate action from its own feed — otherwise the payment is
   * double-counted. Agree with back office who owns the movement.
   */
  syncTo1C?: boolean;
  /**
   * Date-time with offset (`Utl.isoDateTimeLocal`) every payout ledger is
   * backdated to, for a statement that arrived after the event. Omit to date
   * them now. Snapshots taken since then are corrected server-side.
   */
  valueDate?: string;
  /** Echoed from the parse response to record which upload the run came from. */
  fileName?: string;
  fileSha256?: string;
  fileRowCount?: number;
}

/** `already-paid`, `kyc-not-approved` or `zero-net` for a skip; an error message for a failure. */
export interface CorporateActionFailure {
  userId: string;
  status: 'skipped' | 'failed';
  reason?: string;
}

export interface CorporateActionRunLine extends CorporateActionPreviewLine {
  totals?: CorporateActionTotals;
  /** Every holder the desk skipped on this event. Not capped, unlike `failures`. */
  excludedUserIds?: string[];
  /** Capped at 50 for triage; `totals.failed` is the true count. */
  failures?: CorporateActionFailure[];
}

export interface CorporateActionRun {
  id: string;
  type: CorporateActionType;
  status: CorporateActionRunStatus;
  adminId?: string;
  note?: string;
  sourceFile?: CorporateActionSourceFile;
  startedAt?: string | number;
  finishedAt?: string | number;
  /** Instant the payout ledgers were backdated to; null when dated at processing. */
  valueDate?: string | null;
  syncTo1C?: boolean;
  allowCurrentBalanceFallback?: boolean;
  totals?: CorporateActionTotals;
  linesCount?: number;
  /** Omitted by the list endpoint for performance; present on the detail endpoint. */
  lines?: CorporateActionRunLine[];
}

/**
 * What a payout ledger carries in `details`. `entitize()` returns this to
 * clients too, so the gross and the withholding are visible on both sides.
 */
export interface CorporateActionPayoutDetails {
  tickerId?: string;
  sourceId?: string;
  isin?: string;
  eventKey?: string;
  runId?: string;
  /**
   * Dividends carry `dividendDate`, coupons `couponDate`. An ADR fee carries
   * NEITHER — `recordDate` below is its date.
   */
  dividendDate?: string;
  couponDate?: string;
  recordDate?: string;
  snapshotDate?: string;
  holderBasis?: HolderBasis;
  /** Decimal strings — render, do not compute. */
  shares?: string;
  perShare?: string;
  grossAmount?: string;
  taxAmount?: string;
  taxRatePercent?: string;
  currency?: string;
  origin?: CorporateActionOrigin;
  /** ADR fee only, so a derived rate stays traceable to what produced it. */
  perShareSource?: AdrFeePerShareSource;
  feeRatePercent?: string;
  [key: string]: unknown;
}

/**
 * One credited — or, on an ADR fee, debited — holder: the ledger as the
 * transaction feed renders it, plus the client's email.
 *
 * This is the payment history for a run — the run document carries per-line
 * totals only, and its `failures` list covers just what did NOT go through.
 */
export interface CorporateActionPayout {
  id?: string;
  userId?: string;
  email?: string;
  currency?: string;
  /**
   * The NET credited — the tax is not a separate debit. **Negative on an ADR
   * fee**, which is a debit. Rendered as-is; nothing here changes its sign.
   */
  quantity?: string | number;
  created?: string | number;
  /** `dividend`, `bond_coupon` or `adr_fee`. */
  source?: string;
  description?: string;
  details?: CorporateActionPayoutDetails;
}

export interface CorporateActionPayoutParams {
  page: number;
  itemsPerPage: number;
  /** Narrow to one payable event. */
  eventKey?: string;
  /** Narrow to one client. */
  userId?: string;
}

export interface CorporateActionPayoutResponse {
  items: CorporateActionPayout[];
  total: number;
}

export interface CorporateActionListParams {
  page: number;
  itemsPerPage: number;
  type?: string;
  status?: string;
  keyword?: string;
}

export interface CorporateActionListResponse {
  items: CorporateActionRun[];
  total: number;
}

/**
 * The fields preview/execute accept on a line. A parse response line can be
 * posted back unmodified — diagnostics included — and they are ignored and
 * re-derived server-side. But a field name that is NOT accepted is rejected
 * outright rather than dropped, so every payload is built through here instead
 * of by spreading whatever the parser happened to emit.
 *
 * `narration` / `taxNarration` are on the list because the run record stores
 * them; stripping them would lose the raw statement text off the audit trail.
 */
const SUBMITTABLE_LINE_FIELDS: (keyof CorporateActionLine)[] = [
  'eventKey',
  'symbol',
  'isin',
  'instrumentInternalId',
  'perShare',
  'currency',
  'recordDate',
  'snapshotDate',
  'holderBasis',
  'taxRatePercent',
  'hasTaxRow',
  'perShareSource',
  'feeRatePercent',
  'recordDateExplicit',
  'gtnShares',
  'gtnSharesSource',
  'gtnSharesFromCash',
  'gtnTaxRowShares',
  'gtnNarrationShares',
  'gtnFiguresInconsistent',
  'gtnGrossAmount',
  'gtnTaxAmount',
  'instrument',
  'resolvedBy',
  'narration',
  'taxNarration',
  'dialect',
  'sourceRows',
  'origin',
  'excludedUserIds',
];

/** Strips a parsed or previewed line down to what the API will accept. */
export function toSubmittableLine(
  line: CorporateActionLine
): CorporateActionLine {
  const out: CorporateActionLine = {};
  for (const field of SUBMITTABLE_LINE_FIELDS) {
    const value = line[field];
    if (value === undefined) {
      continue;
    }
    // `currency: null` is the one meaningful blank — the newer narration
    // dialect states no currency and the preview resolves it from the
    // instrument, raising CURRENCY_INFERRED. Every other blank means "absent":
    // clearing an nz-select hands back `null`, and a manual line starts its
    // untouched fields as ''. Forwarding either would put a `null` or an empty
    // string on the wire for a field the API expects to be omitted.
    if (value === '' || (value === null && field !== 'currency')) {
      continue;
    }
    // An empty skip list says nothing; leaving it off keeps a line with no
    // skips identical to how it was before skipping existed.
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    // `false` is meaningful on `hasTaxRow` and `recordDateExplicit` — the first
    // is how an ADR fee says it has no tax row, the second how a line says its
    // record date was recovered from `On <date>` rather than stated. Neither is
    // a blank, so neither is stripped: the `=== ''` / `=== null` tests above
    // already let them through, and nothing may be added that does not.
    (out as Record<string, unknown>)[field] = value;
  }
  return out;
}

@Injectable({ providedIn: 'root' })
export class CorporateActionService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`, so these resolve to `/api/admin/corporate-actions`. */
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /** Nothing is persisted. Re-upload as often as you like. */
  parse(payload: ParseStatementPayload): Observable<ParseStatementResponse> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/corporate-actions/parse`,
      payload
    );
  }

  /** Read-only: nothing is persisted, no balance moves. */
  preview(
    payload: PreviewCorporateActionPayload
  ): Observable<CorporateActionPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/corporate-actions/preview`,
      {
        ...payload,
        lines: payload.lines.map(toSubmittableLine),
      }
    );
  }

  /** Irreversible. Only ever call this from a state the preview authorised. */
  run(payload: RunCorporateActionPayload): Observable<CorporateActionRun> {
    return this.networkHelper.post(`${this.hostUrl}/admin/corporate-actions`, {
      ...payload,
      lines: payload.lines.map(toSubmittableLine),
    });
  }

  /**
   * Every holder of one line, paged. Read-only, and resolved exactly as
   * preview resolves it — the figures are the ones that will be paid.
   */
  previewHolders(
    payload: PreviewHoldersPayload
  ): Observable<PreviewHoldersResponse> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/corporate-actions/preview/holders`,
      { ...payload, line: toSubmittableLine(payload.line) }
    );
  }

  list(
    params: CorporateActionListParams
  ): Observable<CorporateActionListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.type) query['type'] = params.type;
    if (params.status) query['status'] = params.status;
    if (params.keyword) query['keyword'] = params.keyword;
    return this.networkHelper.get(`${this.hostUrl}/admin/corporate-actions`, {
      ...query,
    });
  }

  /** Returns the run record with its per-line totals and capped failures. */
  getById(id: string): Observable<CorporateActionRun> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/corporate-actions/${encodeURIComponent(id)}`
    );
  }

  /**
   * Who a run actually paid. Per-holder outcomes are the ledgers themselves —
   * embedding hundreds of events across hundreds of holders would overrun the
   * 16 MB document cap — so they are read back through their own endpoint.
   */
  listPayouts(
    id: string,
    params: CorporateActionPayoutParams
  ): Observable<CorporateActionPayoutResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.eventKey) query['eventKey'] = params.eventKey;
    if (params.userId) query['userId'] = params.userId;
    return this.networkHelper.get(
      `${this.hostUrl}/admin/corporate-actions/${encodeURIComponent(id)}/payouts`,
      { ...query }
    );
  }
}
